// ===========================================
// CPG FORECAST — monthly demand, channels, run rates, what to produce
// ===========================================
// The brand-shaped replacement for the restaurant's weekday prep forecast:
// demand by month per product, the channel split, a next-month / next-quarter
// projection from the trailing run rate, the standing-order committed volume,
// a yearly run rate, and the production implication against stock on hand.
// All computed from wholesale orders — labeled as computed, never "predicted".

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Factory, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import { wholesaleApi, menuApi, inventoryApi, restaurantApi } from '../services/api';
import { formatByCurrency } from '../lib/money';
import { CATEGORY_CHIP } from '../utils/cpg';
import type { EntityView } from './EntityToggle';
import {
  buildDemandModel, ymLong, ymShort, type DemandModel,
} from '../lib/cpgDemand';

const units = (n: number) => `${Math.round(n).toLocaleString('en-US')} unit${Math.round(n) === 1 ? '' : 's'}`;
const n0 = (n: number) => Math.round(n).toLocaleString('en-US');
const Dash = () => <span className="text-slate-300">—</span>;

// ---------- data hook (shared cache keys wherever one already exists) ----------
export function useCpgDemand(entity: EntityView, channel?: string | null) {
  const { data: orders, isPending: ordersPending } = useQuery({
    queryKey: ['cpg-orders', entity],
    queryFn: async () => (await wholesaleApi.getOrders(entity === 'all' ? undefined : { entity })).data?.data || [],
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['wholesale-customers'],
    queryFn: async () => (await wholesaleApi.getCustomers()).data?.data || [],
  });
  const { data: menuData } = useQuery({
    queryKey: ['menu-items-flat'],
    queryFn: async () => {
      const res = await menuApi.getItems();
      return res.data?.data || res.data || [];
    },
    staleTime: 60_000,
  });
  const { data: inventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });
  const { data: restaurant } = useQuery({
    queryKey: ['restaurant-forecast'],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
  });

  const menuItems: any[] = (Array.isArray(menuData) ? menuData : (menuData as any)?.items || []).filter((m: any) => m?.id);
  const costPerCase: Record<string, number> = restaurant?.settings?.costPerCase || {};

  const model = useMemo(
    () => buildDemandModel({
      orders: Array.isArray(orders) ? orders : (orders as any)?.orders || [],
      customers: customers as any[],
      menuItems,
      inventory: inventory as any[],
      costPerCase,
      channel,
      entity,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, customers, menuData, inventory, restaurant, channel, entity]
  );

  return { model, isPending: ordersPending };
}

// Month header label; adds the year when the window spans two.
const useMonthLabel = (months: string[]) => {
  const spansYears = new Set(months.map((m) => m.slice(0, 4))).size > 1;
  return (ym: string) => ymShort(ym, spansYears);
};

// ===========================================
// FULL VIEW (Margins → Forecast tab)
// ===========================================
export function CpgForecastView({ entity }: { entity: EntityView }) {
  const { model, isPending } = useCpgDemand(entity);
  const monthLabel = useMonthLabel(model.months);

  if (isPending) {
    return <div className="card p-10 text-center text-slate-400">Reading your order history…</div>;
  }
  if (model.orderCount === 0) {
    return (
      <div className="card p-10 text-center text-slate-500">
        The forecast builds itself from counted wholesale orders (confirmed through invoiced) — once the
        first order confirms, demand lands here month by month, by product and by channel.
      </div>
    );
  }

  const p = model.projection;
  const basisLabel = model.basisMonths.length
    ? model.basisMonths.map((m) => ymShort(m)).join('–')
    : null;
  const maxMonthUnits = Math.max(...model.months.map((m) => model.unitsByMonth[m] || 0), 1);

  // Production implication: what to produce, what's covered, what has no stock record.
  const rated = model.productsAll.filter((x) => x.runRate != null && x.runRate > 0);
  const toProduce = rated.filter((x) => x.produce != null && x.produce > 0).sort((a, b) => (b.produce! - a.produce!));
  const covered = rated.filter((x) => x.produce === 0);
  const noStock = rated.filter((x) => x.onHand == null);
  const bottleLiters = toProduce.reduce((s, x) => s + (x.litersEach != null ? x.litersEach * (x.produce || 0) : 0), 0);

  return (
    <div className="space-y-5">
      {/* Projection cards — computed run rates, said plainly */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-4">
          <p className="text-xs text-slate-500">{ymLong(p.targetMonth)} — projected demand</p>
          <p className="text-2xl font-bold text-slate-900">{p.unitsPerMonth != null ? units(p.unitsPerMonth) : '—'}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {basisLabel
              ? `${ymShort(model.basisMonths[model.basisMonths.length - 1], true)} pace — average of ${basisLabel}${p.litersPerMonth ? ` · ~${p.litersPerMonth.toLocaleString('en-US')} L` : ''}`
              : 'needs one complete month of orders'}
            {p.recordedThisMonth > 0 && ` · ${n0(p.recordedThisMonth)} already on the books this month`}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500">Next quarter ({ymShort(p.nextQuarterMonths[0], true)} – {ymShort(p.nextQuarterMonths[2], true)})</p>
          <p className="text-2xl font-bold text-slate-900">{p.nextQuarterUnits != null ? units(p.nextQuarterUnits) : '—'}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            same pace, three months out
            {p.bookedNextMonth > 0 && ` · ${n0(p.bookedNextMonth)} already booked for ${ymShort(p.nextQuarterMonths[0])}`}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500">Committed on standing orders</p>
          <p className="text-2xl font-bold text-slate-900">{model.standing.unitsPerWeek > 0 ? `${n0(model.standing.unitsPerWeek)}/week` : '—'}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {model.standing.unitsPerWeek > 0
              ? `${units(model.standing.unitsPerWeek)}/week committed across ${model.standing.customers} account${model.standing.customers === 1 ? '' : 's'} (~${n0(model.standing.unitsPerMonth)}/month) — drafted every week, confirmed by you`
              : 'no standing weekly orders yet — set one from any order on Wholesale'}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-slate-500">Yearly run rate</p>
          <p className="text-2xl font-bold text-slate-900">{model.yearly.unitsAnnualized != null ? `${n0(model.yearly.unitsAnnualized)}/yr` : '—'}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {model.yearly.unitsAnnualized != null
              ? `based on ${model.yearly.monthsUsed} complete month${model.yearly.monthsUsed === 1 ? '' : 's'}, annualized${model.yearly.litersAnnualized ? ` · ~${n0(model.yearly.litersAnnualized)} L` : ''}${Object.keys(model.yearly.revenueAnnualizedByCurrency).length ? ` · ${formatByCurrency(model.yearly.revenueAnnualizedByCurrency)}` : ''}`
              : 'needs one complete month of orders'}
          </p>
        </div>
      </div>
      <p className="text-xs text-slate-400 -mt-2">
        Projections are trailing run rates computed from your counted wholesale orders — nothing here is AI-predicted.
        Standing weekly orders are committed volume and already flow into the order history as they draft and confirm.
      </p>

      {/* Production implication — the useful part of "prep" */}
      {rated.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <Factory className="w-4 h-4 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900">What to produce</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Projected {ymLong(p.targetMonth)} demand per product vs what Inventory says is on the shelf.
          </p>
          {toProduce.length === 0 ? (
            <p className="text-sm text-slate-600">
              Nothing to run — every product with a recent pace is covered by stock on hand.
            </p>
          ) : (
            <div className="space-y-2">
              {toProduce.map((x) => (
                <div key={x.key} className="flex items-baseline justify-between gap-3 border-b border-slate-50 pb-1.5">
                  <span className="text-sm text-slate-900">
                    Produce <span className="font-semibold">~{n0(x.produce!)}</span> × {x.name}
                  </span>
                  <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                    {n0(x.onHand!)} on hand · ~{n0(x.runRate!)} projected
                  </span>
                </div>
              ))}
            </div>
          )}
          {bottleLiters > 0 && (
            <p className="text-xs text-slate-500 mt-3">
              ≈ {n0(bottleLiters)} L to bottle across these runs — sizes parsed from the product names.
            </p>
          )}
          {covered.length > 0 && toProduce.length > 0 && (
            <p className="text-xs text-slate-400 mt-2">
              Covered by stock on hand: {covered.map((x) => `${x.name} (${n0(x.onHand!)} ≥ ~${n0(x.runRate!)})`).join(', ')}.
            </p>
          )}
          {noStock.length > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              No Inventory record for {noStock.map((x) => `${x.name} (~${n0(x.runRate!)} projected)`).join(', ')} — add one and the produce number fills in.
            </p>
          )}
          <p className="text-xs text-slate-400 mt-2">
            On hand comes from Inventory; projected demand is the {basisLabel || 'trailing'} pace from wholesale orders.
          </p>
        </div>
      )}

      {/* Demand by month — totals bar strip + per-product table */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-900">Demand by month</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Units on counted wholesale orders, by the month they fulfill.</p>
        <div className="flex items-end gap-1.5 h-24 mb-1">
          {model.months.map((m) => {
            const v = model.unitsByMonth[m] || 0;
            const future = m > model.currentYm;
            return (
              <div key={m} className="flex-1 flex flex-col items-center justify-end h-full min-w-0" title={`${ymLong(m)}: ${units(v)}${future ? ' (booked ahead)' : ''}`}>
                <span className="text-[10px] text-slate-500 tabular-nums">{v ? n0(v) : ''}</span>
                <div
                  className={clsx('w-full rounded-t', future ? 'bg-blue-200' : m === model.currentYm ? 'bg-primary-300' : 'bg-primary-500')}
                  style={{ height: `${Math.max(v > 0 ? 6 : 0, Math.round((v / maxMonthUnits) * 72))}px` }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex gap-1.5 mb-4">
          {model.months.map((m) => (
            <p key={m} className="flex-1 text-center text-[10px] text-slate-400 truncate">{monthLabel(m)}{m === model.currentYm ? '*' : ''}</p>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                <th className="px-3 py-2 font-medium text-left min-w-[180px]">Product</th>
                {model.months.map((m) => (
                  <th key={m} className="px-2 py-2 font-medium text-right whitespace-nowrap">{monthLabel(m)}{m > model.currentYm ? '†' : ''}</th>
                ))}
                <th className="px-3 py-2 font-medium text-right border-l border-slate-200">Total</th>
              </tr>
            </thead>
            <tbody>
              {model.productsAll.map((x) => (
                <tr key={x.key} className="border-b border-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="font-medium text-slate-900">{x.name}</span>
                    {x.litersEach != null && <span className="block text-[11px] text-slate-400">{x.litersEach} L per unit</span>}
                  </td>
                  {model.months.map((m) => (
                    <td key={m} className="px-2 py-2 text-right tabular-nums text-slate-600">
                      {x.unitsByMonth[m] ? n0(x.unitsByMonth[m]) : <Dash />}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900 border-l border-slate-200">{n0(x.unitsTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          * {ymLong(model.currentYm)} is underway{model.futureMonths.length > 0 ? '; † months are booked ahead — confirmed orders with a future fulfillment date' : ''}.
          Counted statuses: confirmed, partially fulfilled, fulfilled, invoiced. Drafts and cancellations never count.
        </p>
      </div>

      {/* Channel split */}
      {model.channels.length > 0 && (
        <div className="card p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">By channel</h2>
          <p className="text-sm text-slate-500 mb-4">The same demand, split by each customer's sales category.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                  <th className="px-3 py-2 font-medium text-left min-w-[160px]">Channel</th>
                  <th className="px-3 py-2 font-medium text-right">Share</th>
                  {model.months.map((m) => (
                    <th key={m} className="px-2 py-2 font-medium text-right whitespace-nowrap">{monthLabel(m)}</th>
                  ))}
                  <th className="px-3 py-2 font-medium text-right border-l border-slate-200">Total</th>
                </tr>
              </thead>
              <tbody>
                {model.channels.map((c) => {
                  const totalAll = model.channels.reduce((s, x) => s + x.unitsTotal, 0);
                  return (
                    <tr key={c.category} className="border-b border-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={clsx('px-1.5 py-0.5 rounded text-xs font-medium', CATEGORY_CHIP[c.category] || 'bg-slate-100 text-slate-600')}>{c.label}</span>
                        {c.category === 'uncategorized' && <span className="block text-[11px] text-slate-400 mt-0.5">set each customer's category on Wholesale → Customers</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{totalAll > 0 ? `${Math.round((c.unitsTotal / totalAll) * 100)}%` : '—'}</td>
                      {model.months.map((m) => (
                        <td key={m} className="px-2 py-2 text-right tabular-nums text-slate-600">
                          {c.unitsByMonth[m] ? n0(c.unitsByMonth[m]) : <Dash />}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900 border-l border-slate-200">{n0(c.unitsTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Units per month per channel; a channel's revenue in its own currencies: {model.channels.map((c) => `${c.label} ${formatByCurrency(c.revenueByCurrency)}`).join(' · ')}.
          </p>
        </div>
      )}
    </div>
  );
}

// ===========================================
// COMPACT OUTLOOK (Production page, CPG accounts)
// ===========================================
// Replaces the restaurant's "Prep for Tuesday / last 5 Tuesdays" checklist:
// a brand preps by the month, against projected demand and stock on hand.
export function CpgProductionOutlook() {
  const { model, isPending } = useCpgDemand('all');
  if (isPending) return null;

  const rated = model.productsAll.filter((x) => x.runRate != null && x.runRate > 0);
  if (rated.length === 0) return null;
  const toProduce = rated.filter((x) => x.produce != null && x.produce > 0).sort((a, b) => b.produce! - a.produce!);
  const basisLabel = model.basisMonths.map((m) => ymShort(m)).join('–');

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-slate-900">Production outlook — {ymLong(model.currentYm)}</p>
          <p className="text-xs text-slate-500">
            Projected demand at your {basisLabel} pace vs stock on hand — computed from wholesale orders, by the month.
          </p>
        </div>
        <Link to="/margins?view=forecast" className="text-xs text-primary-600 font-medium shrink-0">Full forecast →</Link>
      </div>
      {toProduce.length === 0 ? (
        <p className="text-sm text-slate-600 mt-3">Every product with a recent pace is covered by stock on hand — nothing to run.</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {toProduce.slice(0, 5).map((x) => (
            <div key={x.key} className="flex items-baseline justify-between gap-3 border-b border-slate-50 pb-1">
              <span className="text-sm text-slate-800">Produce <span className="font-semibold">~{n0(x.produce!)}</span> × {x.name}</span>
              <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{n0(x.onHand!)} on hand · ~{n0(x.runRate!)} projected</span>
            </div>
          ))}
          {toProduce.length > 5 && (
            <p className="text-xs text-slate-400">+ {toProduce.length - 5} more on the full forecast.</p>
          )}
        </div>
      )}
    </div>
  );
}

export type { DemandModel };
